import { useEffect, useRef, useState, useContext } from 'react';
import { useOutletContext } from 'react-router-dom';
import Task from './Task';
import { Context } from '../AuthContext';
import { toast } from 'react-toastify';
import { useCategory } from '../CategoryContext';
import Spinner from './Spinner';
import Invitation from './Invitation';
import UpgradeButton from './UpgradeButton';
import { getPlanTier, getJoinedVerticalsLimit } from '../config/planLimits';
import styles from '../pages/AuthPages/Auth.module.css';

const Tasks = ({ activeTab }) => {
  const { user } = useContext(Context);
  const { verticals, refreshVerticals } = useCategory();
  const { subscriptionStatus } = useOutletContext() || {};
  const planTier = getPlanTier(subscriptionStatus);
  const joinedVerticalsLimit = getJoinedVerticalsLimit(subscriptionStatus);
  const joinedCount = (verticals || []).filter((v) => v.status === 'Joined').length;
  const acceptDisabled = planTier === 'free' && joinedCount >= joinedVerticalsLimit;
  const [tasks, setTasks] = useState([]);
  const [polling, setPolling] = useState(false);
  const [loading, setLoading] = useState(true);
  const [invitedVerticals, setInvitedVerticals] = useState([]);
  const [replacementState, setReplacementState] = useState(null);
  const hasInitialized = useRef(false);
  const pendingIdsRef = useRef(new Set());

  const showsReplacementCounter =
    activeTab === 'Private Verticals' || activeTab === 'My Verticals';

  const fetchReplacementState = async () => {
    if (!user?.uid) return;
    try {
      const token = await user.getIdToken();
      const res = await fetch(`${process.env.REACT_APP_FLASK_WEBSERVER}replacement_state`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setReplacementState(data);
      }
    } catch (err) {
      console.error('Failed to fetch replacement state', err);
    }
  };

  useEffect(() => {
    if (!user?.uid) return;
    if (!showsReplacementCounter) {
      setReplacementState(null);
      return;
    }
    fetchReplacementState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, showsReplacementCounter]);

  useEffect(() => {
    if (!hasInitialized.current && verticals.length > 0) {
      setInvitedVerticals(
        verticals.filter(
          (v) => v.status !== 'Created' && v.status !== 'Vertical Deleted' && v.status !== 'Pending'
        )
      );
      hasInitialized.current = true;
    }
  }, [verticals]);

  useEffect(() => {
    if (!user?.uid) return;
    fetchTasks();
    refreshVerticals();
  }, [user?.uid]);

  useEffect(() => {
    let interval = null;
    if (polling) {
      interval = setInterval(() => {
        fetchTasks();
      }, 10000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [polling]);

  useEffect(() => {
    if (!user?.uid) return;

    const currentPendingIds = new Set(
      tasks.filter((t) => t.status === 'Pending').map((t) => t._id)
    );

    // if any previously-pending id is no longer pending, something completed
    let completed = false;
    for (const prevId of pendingIdsRef.current) {
      if (!currentPendingIds.has(prevId)) {
        completed = true;
        break;
      }
    }

    if (completed) {
      refreshVerticals();
    }

    pendingIdsRef.current = currentPendingIds;
  }, [tasks, refreshVerticals, user?.uid]);

  const deleteVertical = async (id, index) => {
    const token = await user.getIdToken();
    const res = await fetch(
      `${process.env.REACT_APP_FLASK_WEBSERVER}delete_vertical/${id}?vertical_id=${id}`,
      {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (res.status === 200) {
      refreshVerticals();
      toast.success('Vertical deleted successfully!', {
        className: 'toast',
        autoClose: 3000,
      });
      setTasks((prev) => prev.filter((task) => task._id !== id));

      // Optimistic local decrement; next navigation re-fetches authoritative state.
      if (replacementState) {
        setReplacementState((prev) => (prev ? { ...prev, used: prev.used + 1 } : prev));
      }
      return;
    }

    if (res.status === 403) {
      let body = {};
      try {
        body = await res.json();
      } catch (_) {
        // ignore
      }
      if (body && body.code === 'replacement_limit_reached') {
        toast.error(
          "You've reached your Vertical Replacement limit. Additional replacements become available after your allowance resets.",
          { className: 'toast', autoClose: 5000 }
        );
        // Re-fetch authoritative state — local counter was stale.
        fetchReplacementState();
      }
    }
  };

  sessionStorage.removeItem('currentTab');

  const fetchTasks = async () => {
    const token = await user.getIdToken();
    const res = await fetch(process.env.REACT_APP_FLASK_WEBSERVER + 'tasks', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!res.ok) {
      setTasks([]);
      setLoading(false);
      setPolling(false);
      return;
    }

    const data = await res.json();
    const nextTasks = Array.isArray(data) ? [...data].reverse() : [];
    setTasks(nextTasks);
    setLoading(false);
    setPolling(nextTasks.some((task) => task.status === 'Pending'));
  };

  let listing;
  if (activeTab === 'Joined Verticals') {
    listing = invitedVerticals;
  } else if (activeTab === 'Private Verticals') {
    listing = tasks.filter((v) => v.invited_count === 0);
  } else if (activeTab === 'My Verticals') {
    listing = tasks.filter((v) => v.invited_count > 0);
  }

  const thead = {
    'Private Verticals': ['Private Vertical Name', 'Submission Time', 'Status', 'Actions'],
    'My Verticals': ['Vertical Name', 'Created At', 'Number of Members', 'Actions'],
    'Joined Verticals': [
      'Joined Vertical Name',
      'Subscribed At',
      'Creator',
      'Permissions',
      'Status',
      'Actions',
    ],
  };

  const displayToast = () => {
    toast.success(
      <>
        Your download will begin momentarily
        <br /> Please do not navigate away from this screen.
      </>,
      {
        className: styles.toast,
        autoClose: 5000,
        position: 'top-center',
        hideProgressBar: true,
        closeButton: false,
        icon: false,
        theme: 'dark',
      }
    );
  };

  const replacementLimit = replacementState?.limit;
  const replacementUsed = replacementState?.used ?? 0;
  const deleteDisabled =
    showsReplacementCounter &&
    replacementState &&
    replacementLimit != null &&
    replacementUsed >= replacementLimit;

  return loading ? (
    <Spinner />
  ) : (
    <div className="result">
      {activeTab === 'Joined Verticals' && planTier === 'free' && (
        <div style={{ textAlign: 'left', margin: '0 0 16px 0' }}>
          <p style={{ margin: 0, padding: 0, fontWeight: 700 }}>
            Joined Verticals: {joinedCount} of {joinedVerticalsLimit}
          </p>
          <p style={{ lineHeight: 1.4, margin: '4px 0 8px 0', fontSize: '12px', color: 'gray' }}>
            Upgrade to Full Access for expanded collaboration capacity and increased Joined Vertical
            access.
          </p>
          <UpgradeButton sx={{ margin: '0 0 12px 0' }} />
        </div>
      )}
      {showsReplacementCounter && replacementState && (
        <div style={{ textAlign: 'left', margin: '0 0 16px 0' }}>
          <p style={{ margin: 0, padding: 0, fontWeight: 700 }}>
            Vertical Replacements Available: {Math.max(replacementLimit - replacementUsed, 0)} of{' '}
            {replacementLimit} (Resets in {replacementState.days_until_reset} days)
          </p>
          {planTier === 'free' && (
            <>
              <p style={{ lineHeight: 1.4, margin: '4px 0 8px 0', fontSize: '12px', color: 'gray' }}>
                Upgrade to Full Access for additional Vertical Replacements and expanded workspace
                flexibility.
              </p>
              <UpgradeButton sx={{ margin: '0 0 12px 0' }} />
            </>
          )}
        </div>
      )}
      <table
        style={{
          width: '100%',
          border: '0px',
          borderCollapse: 'collapse',
        }}
      >
        <thead>
          {thead[activeTab].map((columnName, index) => (
            <th
              key={index}
              style={{
                backgroundColor: 'inherit',
                padding: index === 0 ? '0px 5px' : '0px',
                textAlign: 'left',
              }}
            >
              <h2>{columnName}</h2>
            </th>
          ))}
        </thead>
        <tbody style={{ border: '0px' }}>
          {listing.map((task, index) =>
            activeTab === 'Joined Verticals' ? (
              <Invitation
                key={task.id}
                task={task}
                index={index}
                setInvitedVerticals={setInvitedVerticals}
                displayToast={displayToast}
                acceptDisabled={acceptDisabled}
              />
            ) : (
              <Task
                key={task._id}
                task={task}
                index={index}
                activeTab={activeTab}
                onDelete={deleteVertical}
                displayToast={displayToast}
                deleteDisabled={deleteDisabled}
              />
            )
          )}
        </tbody>
      </table>
    </div>
  );
};

export default Tasks;
